import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { Formik, FormikErrors, FormikHandlers, FormikHelpers, FormikTouched } from 'formik';
import styles from '../AccountingSystemOpen.module.scss';
import useFetchFinancialInstitutionBranches from '../../../../../hooks/useApi/useFetchFinancialInstitutionBranches';
import { buildPaymentUpdateRows, PaymentUpdateRows } from './buildPaymentUpdateRows.helper';
import { PaymentInfoAndUpdate } from '../types';
import { useClientData } from '../../../../../providers/ClientData.provider';
import ElinxConnect from '../../../../ElinxConnect/ElinxConnect';
import { getRequiredIcon, updateCustomerData } from '../utils';
import useFetchRailzBillCreditNotes from '../../../../../hooks/useApi/useFetchRailzBillCreditNotes';
import useFetchRailzInvoiceCreditNotes from '../../../../../hooks/useApi/useFetchRailzInvoiceCreditNotes';
import useDataFetchIndicator from '../../../../../hooks/useDataFetchIndicator';

interface BankingUpdateDialogueProps {
  isVisible: boolean;
  type: 'Payable' | 'Receivable';
  originalValue: vp.TemporaryBillsOrInvoicesRails | undefined;
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  close: () => void;
  onSave: (savedValues: vp.TemporaryBillsOrInvoicesRails) => void;
  BusinessName: string;
  ServiceName: string;
}
type FC = FunctionComponent<BankingUpdateDialogueProps>;

const {
  Modal,
  SubTitle,
  Box,
  Button,
  ElementContainer,
  TextField,
  Select,
  GoogleAddressField,
  Radio,
  DatepickerSimple,
  FilterableMultiSelector,
  Icon,
} = vp;

const BankingUpdateDialogue: FC = ({
  isVisible,
  type,
  close,
  onSave,
  financialInstitutions,
  originalValue,
  BusinessName,
  ServiceName,
}: BankingUpdateDialogueProps) => {
  const [creditNotes, setCreditNotes] = useState<vp.CreditNote[]>([]);
  const [, , fetchRailzBillsCreditNotes] = useFetchRailzBillCreditNotes({});
  const [, , fetchRailzInvoiceCreditNotes] = useFetchRailzInvoiceCreditNotes({});
  const defaultValues: PaymentInfoAndUpdate = {
    PaymentDate: Date.now().toString(),
    PaymentAmount: '0.00',
    PaymentMethod: '',
    InteracMethod: 'Email Address',
    CreditNotes: '',
    CreditAmount: 0,
  };
  const [currentValues, setCurrentValues] = useState<PaymentInfoAndUpdate>(defaultValues);
  const [showElinxConnect, setShowElinxConnect] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  useEffect(() => {
    const fetchData = async () => {
      if (isVisible) {
        setDataFetchingInfo(true, 'loading some data', 'fullScreen');
        if (type === 'Receivable') {
          if (originalValue?.customerRef?.id) {
            const creditsResponse = await fetchRailzInvoiceCreditNotes({
              BusinessName,
              ServiceName,
              CustomerRefID: originalValue?.customerRef?.id,
            });
            if (creditsResponse.Success && creditsResponse.CreditNotes) {
              setCreditNotes(creditsResponse.CreditNotes);
            } else {
              setCreditNotes([]);
            }
          }
        } else if (originalValue?.vendorRef?.id) {
          const creditsResponse = await fetchRailzBillsCreditNotes({
            BusinessName,
            ServiceName,
            VendorRefID: originalValue?.vendorRef?.id,
          });
          if (creditsResponse.Success && creditsResponse.CreditNotes) {
            setCreditNotes(creditsResponse.CreditNotes);
          } else {
            setCreditNotes([]);
          }
        }
        setDataFetchingInfo(false);
      } else {
        setCreditNotes([]);
      }
    };

    fetchData();
  }, [isVisible]);
  const {
    response: { Country },
  } = useClientData();
  const [financialInstitutionBranchesResponse, fetchFinancialInstitutionBranches] =
    useFetchFinancialInstitutionBranches({});
  const [elinxConnectSettings, setElinxConnectSettings] = useState<vp.EmbedUrlRequest>({
    RedirectURL: 'https://vopay.com',
    Country,
    PaymentSelectionMethod: 'credit',
    ClientControlled: false,
    RedirectMethod: 'JavascriptMessage',
  });
  const totalAmount = Number(originalValue?.amountDue) || Number(originalValue?.totalAmount) || 0;
  const paymentUpdateRows = buildPaymentUpdateRows({
    type,
    country: Country,
    totalAmount,
    currency: originalValue?.currency || '',
    branchTransitNumbers: Object.values(financialInstitutionBranchesResponse.Branches || {}).map((value) => {
      const { BranchTransitNumber, Address, TownCity, PostalCode, Province } = value;
      return {
        label: `${BranchTransitNumber} ${Address}, ${TownCity}, ${PostalCode}, ${Province}`,
        value: BranchTransitNumber.toString(),
        renderOption: (
          <div>
            {' '}
            {BranchTransitNumber} <br /> {`${Address}, ${TownCity}, ${PostalCode}, ${Province}`}
          </div>
        ),
      };
    }),
    financialInstitutions: Object.values(financialInstitutions || {}).map((value) => ({
      label: `${value.FinancialInstitutionNumber} ${value.FIName}`,
      value: value.FinancialInstitutionNumber,
      renderOption: (
        <div>
          {value.FinancialInstitutionNumber} <br /> {value.FIName}
        </div>
      ),
    })),
    creditNotes,
  });
  const saveForm = (savedValues: PaymentInfoAndUpdate) => {
    if (originalValue) {
      onSave(
        updateCustomerData(
          savedValues,
          originalValue,
          Country,
          type,
          savedValues.PaymentAmount,
          new Date(Number(savedValues.PaymentDate)).toLocaleDateString('en-ca'),
          savedValues.CreditNotes,
        ),
      );
    }
  };
  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={(values, { resetForm }) => {
        saveForm(values);
        resetForm();
      }}
      validationSchema={Yup.object().shape(getValidationSchema(currentValues, paymentUpdateRows))}
      validateOnChange
      validateOnBlur
    >
      {({
        values,
        touched,
        errors,
        isValid,
        handleChange,
        handleBlur,
        handleSubmit,
        setValues,
        setTouched,
        setFieldValue,
        resetForm,
        submitForm,
        validateForm,
      }) => {
        useEffect(() => {
          if (values.FinancialInstitutionNumber) {
            const FinancialInstitutionID = getFinancialInstitutionIDByNumber(values.FinancialInstitutionNumber);
            if (FinancialInstitutionID) {
              fetchFinancialInstitutionBranches({ FinancialInstitutionID: Number(FinancialInstitutionID) });
            }
          }
        }, [values.FinancialInstitutionNumber]);
        useEffect(() => {
          setCurrentValues({
            ...values,
          });
        }, [values]);
        useEffect(() => {
          validateForm();
        }, [values, errors, paymentUpdateRows]);
        useEffect(() => {
          setFieldValue('PaymentAmount', String(totalAmount - values.CreditAmount) || '');
        }, [values.PaymentDate, values.CreditNotes, isVisible]);
        useEffect(() => {
          if (isVisible) {
            setFieldValue('PaymentMethod', '');
          } else {
            setFieldValue('CreditNotes', '');
            setFieldValue('CreditAmount', 0);
          }
          validateForm();
        }, [isVisible]);

        return (
          <>
            <Modal isVisible={isVisible} enableBgClick={false}>
              <div className={[styles.dialogue, styles.dialogueContent].join(' ')}>
                <form onSubmit={handleSubmit} className={styles.dialogueForm}>
                  <Icon
                    className={styles.closeButton}
                    icon="cross"
                    height="12px"
                    width="12px"
                    onClick={() => {
                      resetForm();
                      validateForm();
                      close();
                    }}
                  />

                  <Box base="8" gap="lg" flx="col">
                    <SubTitle subTitle={`${type === 'Receivable' ? 'Collect Receivable' : 'Send Payment'}`} center />

                    <Box width="100%" gap="ml" base="8" flx="col">
                      {paymentUpdateRows
                        .filter((element) => element.visible(values))
                        .map((element) =>
                          getFormElement(
                            element,
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setValues,
                            setTouched,
                            setFieldValue,
                          ),
                        )}
                      <div className={styles.greyCard}>
                        <div className={styles.greyCardRemainingContainer}>
                          <div className={styles.greyCardLabel}>Remaining</div>
                          <div className={styles.greyCardValue}>
                            {originalValue?.currency}{' '}
                            {vp.formattedCurrency(
                              String(
                                Math.round(
                                  totalAmount -
                                    Number(vp.sanitizeToNumeric(String(values.PaymentAmount))) -
                                    values.CreditAmount,
                                ) < //rounded due to floating point precision errors
                                  0
                                  ? vp.formattedCurrency(String(totalAmount))
                                  : vp.formattedCurrency(
                                      String(
                                        Math.abs(
                                          totalAmount -
                                            Number(vp.sanitizeToNumeric(String(values.PaymentAmount))) -
                                            values.CreditAmount,
                                        ), //due to floating point precision errors
                                      ),
                                    ),
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    </Box>

                    <div className={styles.dialogueTerm}>
                      Account information is saved once the {type === 'Receivable' ? 'collection' : 'payment'} is sent.
                    </div>

                    {values.PaymentMethod !== 'Credit Card' && values.PaymentMethod !== 'Debit Card' && (
                      <Button
                        id="save-and-collect"
                        label={type === 'Receivable' ? 'Collect' : 'Pay'}
                        type="submit"
                        color="primary"
                        isDisabled={!isValid}
                        width="100%"
                        height="36px"
                      />
                    )}
                    {values.PaymentMethod === 'Credit Card' && (
                      <Button
                        id="save-and-collect"
                        label="Provide Credit Card Details"
                        type="button"
                        color="primary"
                        height="36px"
                        width="100%"
                        isDisabled={!isValid}
                        onClick={() => {
                          setElinxConnectSettings({
                            RedirectURL: 'https://vopay.com',
                            Country,
                            PaymentSelectionMethod: 'credit',
                            ClientControlled: false,
                            RedirectMethod: 'JavascriptMessage',
                          });
                          setShowElinxConnect(true);
                        }}
                      />
                    )}
                    {values.PaymentMethod === 'Debit Card' && (
                      <Button
                        id="save-and-collect"
                        label="Provide Debit Card Details"
                        type="button"
                        color="primary"
                        height="36px"
                        width="100%"
                        isDisabled={!isValid}
                        onClick={() => {
                          setElinxConnectSettings({
                            RedirectURL: 'https://vopay.com',
                            Country,
                            PaymentSelectionMethod: 'debitcard',
                            ClientControlled: false,
                            RedirectMethod: 'JavascriptMessage',
                          });
                          setShowElinxConnect(true);
                        }}
                      />
                    )}
                  </Box>
                </form>
              </div>
            </Modal>
            {showElinxConnect && (
              <ElinxConnect
                setting={elinxConnectSettings}
                onSuccess={(eventValues) => {
                  if (values.PaymentMethod === 'Debit Card') {
                    setValues({ ...values, TokenDebit: eventValues.data.Token });
                  } else {
                    setValues({ ...values, TokenCredit: eventValues.data.Token });
                  }

                  void submitForm();
                  setShowElinxConnect(false);
                }}
                onClose={() => {
                  setShowElinxConnect(false);
                }}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
  function getFormElement(
    element: PaymentUpdateRows,
    values: PaymentInfoAndUpdate,
    errors: FormikErrors<PaymentInfoAndUpdate>,
    touched: FormikTouched<PaymentInfoAndUpdate>,
    handleChange: FormikHandlers['handleChange'],
    handleBlur: FormikHandlers['handleBlur'],
    setValues: FormikHelpers<PaymentInfoAndUpdate>['setValues'],
    setTouched: FormikHelpers<PaymentInfoAndUpdate>['setTouched'],
    setFieldValue: FormikHelpers<PaymentInfoAndUpdate>['setFieldValue'],
  ) {
    const {
      type: elementType,
      id,
      preset,
      placeholder,
      label,
      showValue,
      require: required,
      getSelectOptions,
      getRadioOptions,
      container,
      paymentRows,
      inputWidth,
      min,
      getLeftElement,
      getRightElement,
      disabled,
      disabledMessage,
      renderOption,
      filterable,
    } = element;
    if (container === 'inputs') {
      const isRequired = required(values);
      return (
        <Box flx="col" width="100%">
          <ElementContainer
            width="100%"
            padding="0 0 0"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" width="100%">
                {paymentRows
                  .filter((subElement) => subElement.visible(values))
                  .map((subElement) =>
                    getFormElement(
                      subElement,
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setValues,
                      setTouched,
                      setFieldValue,
                    ),
                  )}
              </Box>
            }
          />
        </Box>
      );
    }
    if (container === 'wrapper' || id === undefined) {
      return (
        <Box gap="sm" ai="fs" width="100%">
          {paymentRows
            .filter((subElement) => subElement.visible(values))
            .map((subElement) =>
              getFormElement(
                subElement,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setValues,
                setTouched,
                setFieldValue,
              ),
            )}
        </Box>
      );
    }
    const isRequired = required(values);

    switch (elementType) {
      case 'input':
        return (
          <ElementContainer
            width="100%"
            padding="0 0 6px"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" gapSize="6px">
                <TextField
                  variant="outlined"
                  preset={preset}
                  name={id}
                  placeholder={placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputWidth={inputWidth}
                  disabled={disabled ? disabled(values) : false}
                  elementLeft={getLeftElement ? getLeftElement(values) : undefined}
                  elementRight={getRightElement ? getRightElement(values) : undefined}
                  width="100%"
                  height="37px"
                  value={String(values[id] || '')}
                  error={errors[id] && touched[id] ? errors[id]?.toString() : null}
                />
                {disabled && disabled(values) && disabledMessage && (
                  <div className={styles.disabledMessage}>{disabledMessage}</div>
                )}
              </Box>
            }
          />
        );
      case 'select':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            input={
              <Select
                version="2"
                style={{
                  '--triggerFontSize': '14px',
                  '--triggerBorderRadius': '4px',
                }}
                renderOption={renderOption}
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                filterable={filterable}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );
      case 'googleAddress':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            input={
              <GoogleAddressField
                variant="outlined"
                ISOCountry={['US']}
                preset={preset}
                placeholder=""
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                value={String(values[id] || '')}
                error={errors[id] && touched[id] ? errors[id]?.toString() : null}
                onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
                  handleChange(vp.createSyntheticEvent('Address', addressComponents.address));
                  handleChange(vp.createSyntheticEvent('City', addressComponents.city));
                  handleChange(vp.createSyntheticEvent('State', addressComponents.province));
                  handleChange(vp.createSyntheticEvent('ZipCode', addressComponents.postalcode));
                }}
              />
            }
          />
        );
      case 'radio':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Radio
                key={id}
                name={id}
                value={String(values[id])}
                options={getRadioOptions ? getRadioOptions(values) : {}}
                noOrdering
                onBlur={() => {}}
                onChange={handleChange}
                variant="inline"
                setCheckedValue={(value) => {
                  setValues({ ...values, Language: value as 'en' | 'fr' });
                }}
              />
            }
          />
        );

      case 'selectFinanacialInstitution':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Select
                version="2"
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                renderOption={renderOption}
                onBlur={handleBlur}
                onChange={(e) => {
                  setValues({ ...values, BankTransitNumber: '' });
                  setTouched({ ...touched, BankTransitNumber: false });
                  handleChange(e);
                }}
                filterable={filterable}
                style={{
                  '--triggerFontSize': '14px',
                  '--triggerBorderRadius': '4px',
                }}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );
      case 'multiSelect':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            padding="0 0 6px"
            input={
              <FilterableMultiSelector
                variant="outline"
                isNew={false}
                items={getSelectOptions ? (getSelectOptions(values) as vp.SelectOption[]) : []}
                placeholder={placeholder || ''}
                disabledLabel={placeholder || ''}
                name={id}
                position="fixed"
                value={creditNotes
                  .map(
                    (creditNote) =>
                      ({
                        label: `${creditNote.currency} ${vp.formattedCurrency(String(creditNote.remainingCredit))}`,
                        value: creditNote.id,
                      }) as vp.SelectOption<Record<string, never>>,
                  )
                  .filter((item) => (values[id] || '').split(',').includes(item.value))}
                error={errors[id] && touched[id] ? errors[id] : null}
                disabled={disabled ? disabled(values) : false}
                isDisabled={disabled ? disabled(values) : false}
                nosort
                onSelect={(selected) => {
                  setFieldValue(id, selected.map((item) => item.value).join(','));
                  setFieldValue(
                    'CreditAmount',
                    creditNotes
                      .filter((item) => selected.map((itemMap) => itemMap.value).includes(item.id))
                      .reduce((acc, item) => acc + item.remainingCredit, 0),
                  );
                }}
              />
            }
          />
        );

      case 'date':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            isBold={false}
            padding="0 0 6px"
            justifyContent="flex-start"
            input={
              <DatepickerSimple
                variant="outline"
                name={id}
                value={String(values[id] || '')}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] ? errors[id]?.toString() : null}
                min={vp.createTimestamp(min)}
              />
            }
          />
        );
      default:
        throw new Error(`Invalid payment type: ${type}`);
    }
  }
  function getValidationSchema(
    ctx: PaymentInfoAndUpdate,
    rows: PaymentUpdateRows[],
    initialValidationSchema?: { [key: string]: any },
  ): { [key: string]: any } {
    const validations = rows.reduce(
      (existingFields, row) =>
        row.id && row.container === 'field'
          ? {
              ...existingFields,
              [row.id]: row.validate ? row.validate(ctx) : undefined,
            }
          : { ...existingFields, ...getValidationSchema(ctx, row.paymentRows) },
      { ...initialValidationSchema },
    );

    return validations;
  }

  function getFinancialInstitutionIDByNumber(number: string) {
    for (const key in financialInstitutions) {
      if (financialInstitutions[key].FinancialInstitutionNumber.toString() === number) {
        return financialInstitutions[key].FinancialInstitutionID;
      }
    }
    return null; // Return null if the number is not found
  }
};

export default BankingUpdateDialogue;
